/**
 * CSV Preview Modal
 * Shows preview of extracted questions from CSV files before creating them
 */

import React, { useState } from 'react';

// Define ExtractedQuestion type locally to avoid import issues
interface ExtractedQuestion {
  question_type: 'multiple_choice' | 'multi_select' | 'true_false' | 'short_answer';
  prompt_text: string;
  points_possible: number;
  explanation?: string | null;
  category?: string | null;
  allow_partial_credit?: boolean; // For multi_select: if true, awards partial credit
  answer_options: {
    option_text: string;
    is_correct: boolean;
    sequence_order: number;
    partial_credit_weight?: number; // For rating scales
  }[];
}

interface CSVPreviewModalProps {
  questions: ExtractedQuestion[];
  onSave: (questions: ExtractedQuestion[]) => void;
  onCancel: () => void;
  isSaving?: boolean;
  errors?: string[];
  warnings?: string[];
  parsingMethod?: 'structured' | 'ai';
}

export const CSVPreviewModal: React.FC<CSVPreviewModalProps> = ({
  questions,
  onSave,
  onCancel,
  isSaving = false,
  errors = [],
  warnings = [],
  parsingMethod,
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedQuestions, setEditedQuestions] = useState<ExtractedQuestion[]>(questions);

  // Update editedQuestions when questions prop changes
  // Also infer and set allow_partial_credit for multi-select questions where points = options
  React.useEffect(() => {
    const updatedQuestions = questions.map((q) => {
      // For multi-select questions, infer partial credit if points = number of options
      if (q.question_type === 'multi_select' && q.allow_partial_credit !== true) {
        const optionCount = q.answer_options.length;
        if (q.points_possible === optionCount && optionCount > 1) {
          return { ...q, allow_partial_credit: true };
        }
      }
      return q;
    });
    setEditedQuestions(updatedQuestions);
  }, [questions]);

  const handleSave = () => {
    onSave(editedQuestions);
  };

  const handleEditQuestion = (index: number) => {
    setEditingIndex(index);
  };

  const handleSaveEdit = (index: number, updatedQuestion: ExtractedQuestion) => {
    const newQuestions = [...editedQuestions];
    newQuestions[index] = updatedQuestion;
    setEditedQuestions(newQuestions);
    setEditingIndex(null);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
  };

  const handleDeleteQuestion = (index: number) => {
    if (confirm('Are you sure you want to remove this question from the preview?')) {
      const newQuestions = editedQuestions.filter((_, i) => i !== index);
      setEditedQuestions(newQuestions);
    }
  };

  const formatQuestionType = (type: string) => {
    return type.replaceAll('_', ' ').replaceAll(/\b\w/g, (char) => char.toUpperCase());
  };

  // Check if a question is a rating scale (has partial_credit_weight values)
  const isRatingScale = (question: ExtractedQuestion): boolean => {
    return question.answer_options.some(
      opt => opt.partial_credit_weight !== undefined && opt.partial_credit_weight > 0
    );
  };

  // Check if a multi-select question has partial credit enabled
  const hasPartialCredit = (question: ExtractedQuestion): boolean => {
    if (question.question_type !== 'multi_select') return false;
    
    // Check if explicitly set
    if (question.allow_partial_credit === true) {
      return true;
    }
    
    // Infer from points matching option count
    if (question.allow_partial_credit === undefined || question.allow_partial_credit === false) {
      const optionCount = question.answer_options.length;
      if (question.points_possible === optionCount && optionCount > 1) {
        return true;
      }
    }
    
    return false;
  };

  // Get unique errors (remove duplicates)
  const uniqueErrors = Array.from(new Set(errors));
  const uniqueWarnings = Array.from(new Set(warnings));

  const questionPlural = editedQuestions.length !== 1 ? 's' : '';
  const saveButtonLabel = isSaving
    ? 'Saving...'
    : `Save ${editedQuestions.length} Question${questionPlural}`;

  return (
    <div
      className="modal-overlay"
      onClick={isSaving ? undefined : onCancel}
      style={{
        pointerEvents: 'auto',
        zIndex: 1000
      }}
    >
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        style={{
          maxWidth: '900px', 
          maxHeight: '90vh', 
          overflow: 'auto', 
          opacity: isSaving ? 0.5 : 1,
        }}
      >
        <div className="modal-header">
          <h2>Preview CSV Import ({editedQuestions.length} questions)</h2>
          <button 
            className="modal-close" 
            onClick={onCancel}
            disabled={isSaving}
            style={{ opacity: isSaving ? 0.5 : 1, cursor: isSaving ? 'not-allowed' : 'pointer' }}
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {/* Parsing method indicator */}
          {parsingMethod && (
            <div style={{ 
              marginBottom: '1rem', 
              padding: '0.75rem', 
              backgroundColor: parsingMethod === 'structured' ? '#e8f5e9' : '#fff3e0',
              border: `1px solid ${parsingMethod === 'structured' ? '#4caf50' : '#ff9800'}`,
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}>
              <strong>Parsing Method:</strong> {parsingMethod === 'structured' 
                ? '✅ Structured parsing (fast, reliable)' 
                : '🤖 AI-powered parsing (flexible format handling)'}
            </div>
          )}

          {/* Errors */}
          {uniqueErrors.length > 0 && (
            <div style={{ 
              marginBottom: '1rem', 
              padding: '0.75rem', 
              backgroundColor: '#ffebee', 
              border: '1px solid #f44336',
              borderRadius: '4px'
            }}>
              <strong style={{ color: '#c62828' }}>Errors ({uniqueErrors.length}):</strong>
              <ul style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
                {uniqueErrors.map((error) => (
                  <li key={error} style={{ color: '#c62828', marginBottom: '0.25rem' }}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Warnings */}
          {uniqueWarnings.length > 0 && (
            <div style={{ 
              marginBottom: '1rem', 
              padding: '0.75rem', 
              backgroundColor: '#fff3e0', 
              border: '1px solid #ff9800',
              borderRadius: '4px'
            }}>
              <strong style={{ color: '#e65100' }}>Warnings ({uniqueWarnings.length}):</strong>
              <ul style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
                {uniqueWarnings.map((warning) => (
                  <li key={warning} style={{ color: '#e65100', marginBottom: '0.25rem' }}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          {editedQuestions.length === 0 ? (
            <div className="empty-state">
              No questions to preview
              {uniqueErrors.length > 0 && (
                <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
                  Please review the errors above and try again.
                </div>
              )}
            </div>
          ) : (
            <div className="questions-preview">
              {editedQuestions.map((question, index) => (
                <div key={index} className="question-preview-card" style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}>
                  {editingIndex === index ? (
                    <QuestionEditor
                      question={question}
                      onSave={(updated) => handleSaveEdit(index, updated)}
                      onCancel={handleCancelEdit}
                    />
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                        <div>
                          <span style={{ fontWeight: 'bold', marginRight: '0.5rem' }}>Question {index + 1}</span>
                          <span style={{ color: '#666', fontSize: '0.9rem' }}>
                            {formatQuestionType(question.question_type)} • {question.points_possible} points
                          </span>
                          {isRatingScale(question) && (
                            <span 
                              style={{ 
                                marginLeft: '0.5rem',
                                padding: '0.2rem 0.5rem',
                                backgroundColor: '#e8f4f8',
                                color: '#3498db',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                border: '1px solid #3498db'
                              }}
                              title="This is a rating scale question. Points are awarded based on the selected option's weight."
                            >
                              📊 Rating Scale
                            </span>
                          )}
                          {hasPartialCredit(question) && (
                            <span 
                              style={{ 
                                marginLeft: '0.5rem',
                                padding: '0.2rem 0.5rem',
                                backgroundColor: '#fff4e6',
                                color: '#f39c12',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                border: '1px solid #f39c12'
                              }}
                              title="This multi-select question uses partial credit. Points are awarded proportionally based on the number of correct selections."
                            >
                              ⚖️ Partial Credit
                            </span>
                          )}
                        </div>
                        <div>
                          <button
                            onClick={() => handleEditQuestion(index)}
                            className="btn btn-small btn-secondary"
                            style={{ marginRight: '0.5rem' }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteQuestion(index)}
                            className="btn btn-small btn-danger"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      <div style={{ marginBottom: '0.5rem' }}>
                        <strong>Prompt:</strong> {question.prompt_text}
                      </div>
                      {question.category && (
                        <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                          <strong>Category:</strong> {question.category}
                        </div>
                      )}
                      {question.explanation && (
                        <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                          <strong>Explanation:</strong> {question.explanation}
                        </div>
                      )}
                      {question.answer_options.length > 0 && (
                        <div>
                          <strong>Options:</strong>
                          <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                            {question.answer_options.map((option, optIndex) => (
                              <li key={optIndex} style={{ marginBottom: '0.25rem' }}>
                                {option.option_text}
                                {option.is_correct && (
                                  <span style={{ marginLeft: '0.5rem', color: 'green', fontWeight: 'bold' }}>✓ Correct</span>
                                )}
                                {option.partial_credit_weight !== undefined && option.partial_credit_weight > 0 && (
                                  <span 
                                    style={{ 
                                      marginLeft: '0.5rem', 
                                      color: '#3498db', 
                                      fontSize: '0.85rem',
                                      fontWeight: '600'
                                    }}
                                    title={`This option awards ${(option.partial_credit_weight * 100).toFixed(0)}% of points (${(option.partial_credit_weight * question.points_possible).toFixed(1)} points)`}
                                  >
                                    ({((option.partial_credit_weight * question.points_possible).toFixed(1))} pts)
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                          {isRatingScale(question) && (
                            <div style={{ 
                              marginTop: '0.5rem', 
                              padding: '0.5rem', 
                              backgroundColor: '#f0f8ff', 
                              borderRadius: '4px',
                              fontSize: '0.85rem',
                              color: '#2c3e50',
                              border: '1px solid #b3d9ff'
                            }}>
                              <strong>ℹ️ Rating Scale:</strong> Points are awarded based on the selected option. Higher values on the scale award more points.
                            </div>
                          )}
                          {hasPartialCredit(question) && (
                            <div style={{ 
                              marginTop: '0.5rem', 
                              padding: '0.5rem', 
                              backgroundColor: '#fffbf0', 
                              borderRadius: '4px',
                              fontSize: '0.85rem',
                              color: '#2c3e50',
                              border: '1px solid #f39c12'
                            }}>
                              <strong>ℹ️ Partial Credit:</strong> Points are awarded proportionally based on correct selections. For example, selecting 3 out of 5 correct options awards 60% of the total points.
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
          <button 
            onClick={onCancel} 
            className="btn btn-secondary"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="btn btn-primary"
            disabled={editedQuestions.length === 0 || isSaving}
          >
            {saveButtonLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// Simple inline question editor for preview
interface QuestionEditorProps {
  question: ExtractedQuestion;
  onSave: (question: ExtractedQuestion) => void;
  onCancel: () => void;
}

const QuestionEditor: React.FC<QuestionEditorProps> = ({ question, onSave, onCancel }) => {
  const [edited, setEdited] = useState<ExtractedQuestion>(question);

  const handleSave = () => {
    onSave(edited);
  };

  return (
    <div>
      <div style={{ marginBottom: '0.5rem' }}>
        <label htmlFor="csv-edit-prompt-text" style={{ display: 'block', marginBottom: '0.25rem' }}>Prompt Text:</label>
        <input
          id="csv-edit-prompt-text"
          type="text"
          value={edited.prompt_text}
          onChange={(e) => setEdited({ ...edited, prompt_text: e.target.value })}
          style={{ width: '100%', padding: '0.25rem' }}
        />
      </div>
      <div style={{ marginBottom: '0.5rem' }}>
        <label htmlFor="csv-edit-points" style={{ display: 'block', marginBottom: '0.25rem' }}>Points:</label>
        <input
          id="csv-edit-points"
          type="number"
          step="0.1"
          value={edited.points_possible}
          onChange={(e) => setEdited({ ...edited, points_possible: Number.parseFloat(e.target.value) || 0 })}
          style={{ width: '100px', padding: '0.25rem' }}
        />
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
        <button onClick={handleSave} className="btn btn-small btn-primary">
          Save
        </button>
        <button onClick={onCancel} className="btn btn-small btn-secondary">
          Cancel
        </button>
      </div>
    </div>
  );
};
